import type { Platform, LifecycleState } from './types'
import { externalUrl, emailUrl, internalPath, publicUrl, safeFilename } from './links'

/** Lazy browser access keeps importing UI safe during legacy server rendering. */
export function createWebPlatform(publicOrigin?: string): Platform {
  const state = (): LifecycleState => ({ active: typeof document === 'undefined' || document.visibilityState !== 'hidden', online: typeof navigator === 'undefined' || navigator.onLine !== false })
  return {
    kind: 'web', secureStorage: null,
    get capabilities() {
      return {
        camera: typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia),
        microphone: typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia),
        screenShare: typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia),
        pictureInPicture: typeof document !== 'undefined' && Boolean(document.pictureInPictureEnabled),
        notifications: typeof Notification !== 'undefined', backgroundCalls: false,
      }
    },
    clipboard: {
      async writeText(text) {
        if (navigator.clipboard) { await navigator.clipboard.writeText(text); return }
        // Retain copying on the existing HTTP development deployment.
        const focused = document.activeElement
        const input = document.createElement('textarea')
        input.value = text; input.style.position = 'fixed'; input.style.opacity = '0'
        document.body.append(input)
        try {
          input.select()
          if (!document.execCommand('copy')) throw new Error('Clipboard is unavailable')
        } finally { input.remove(); if (focused instanceof HTMLElement) focused.focus() }
      },
    },
    files: {
      async save(blob, filename) {
        const url = URL.createObjectURL(blob)
        try {
          const link = document.createElement('a')
          link.href = url; link.download = safeFilename(filename)
          document.body.append(link); link.click(); link.remove()
        } finally { setTimeout(() => URL.revokeObjectURL(url), 30000) }
      },
      pick(options = {}) {
        return new Promise(resolve => {
          const input = document.createElement('input')
          input.type = 'file'; input.accept = options.accept || ''; input.multiple = options.multiple || false
          input.hidden = true
          const done = (files: File[]) => { input.remove(); resolve(files) }
          input.addEventListener('change', () => done(Array.from(input.files || [])), { once: true })
          input.addEventListener('cancel', () => done([]), { once: true })
          document.body.append(input); input.click()
        })
      },
      async share(file) {
        if (!navigator.canShare?.({ files: [file] })) throw new Error('File sharing is unavailable on this platform')
        await navigator.share({ files: [file] })
      },
    },
    navigation: {
      async openExternal(value) { window.open(externalUrl(value).href, '_blank', 'noopener,noreferrer') },
      async composeEmail(to, subject) { window.location.assign(emailUrl(to, subject)) },
      openInternal(value) { window.open(internalPath(value), '_blank', 'noopener,noreferrer') },
      publicUrl(value) { return publicUrl(publicOrigin || window.location.origin, value) },
      currentPublicUrl() { return publicUrl(publicOrigin || window.location.origin, window.location.pathname + window.location.search + window.location.hash) },
      href: internalPath,
      documentPathname() {
        if (typeof window === 'undefined') return null
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
        return navigation ? new URL(navigation.name).pathname : window.location.pathname
      },
      assign(value) { window.location.assign(internalPath(value)) },
      replace(value) { window.location.replace(internalPath(value)) },
    },
    lifecycle: {
      current: state,
      subscribe(listener) {
        const notify = () => listener(state())
        document.addEventListener('visibilitychange', notify)
        window.addEventListener('online', notify); window.addEventListener('offline', notify)
        return () => {
          document.removeEventListener('visibilitychange', notify)
          window.removeEventListener('online', notify); window.removeEventListener('offline', notify)
        }
      },
    },
  }
}
